import { useNavigation } from '../contexts/NavigationContext'
import { Building2, Loader2 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useGetCompanyBalanceQuery } from '../generated/graphql'
import { RecentActivity } from './swaps/RecentActivity'
import { Holdings } from './swaps/Holdings'
import { PendingActivity } from './swaps/PendingActivity'
import { toast } from 'sonner'

export function SwapModeContent() {
  const { selectedCompanyId, selectedCompany } = useNavigation()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Fetch company balance
  const [{ data: balanceData, fetching: balanceLoading }, refetchBalance] =
    useGetCompanyBalanceQuery({
      variables: { companyId: selectedCompanyId || '' },
      pause: !selectedCompanyId,
    })

  // Ref to store the interval ID for cleanup
  const refetchIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-refetch balance every 10 seconds when company is selected
  useEffect(() => {
    // Clear any existing interval
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current)
      refetchIntervalRef.current = null
    }

    // Only auto-refetch if we have a selected company
    if (selectedCompanyId) {
      // Refetch every 10 seconds for balance updates
      refetchIntervalRef.current = setInterval(() => {
        refetchBalance({ requestPolicy: 'network-only' })
      }, 10000)
    }

    // Cleanup function
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current)
        refetchIntervalRef.current = null
      }
    }
  }, [selectedCompanyId, refetchBalance])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      refetchBalance({ requestPolicy: 'network-only' })
      toast.success('Balance refreshed successfully')
    } catch (error) {
      console.error('Error refreshing balance:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  if (!selectedCompanyId || !selectedCompany) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No company selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please select a company to view swap options.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Company Header with Balance */}
      <div className="space-y-2">
        <div>
          <p className="text-md text-gray-600">Approximate Balance</p>
          {balanceLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-2xl font-bold text-gray-600">Loading...</span>
            </div>
          ) : (
            <h1 className="text-5xl font-bold text-gray-900 flex items-center gap-2">
              {balanceData?.getCompanyBalance?.totalUsdValue || '$0'}{' '}
              <span className="text-2xl font-semibold">USD</span>
            </h1>
          )}
        </div>
      </div>

      {/* Holdings Section */}
      <Holdings
        companyId={selectedCompanyId || ''}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      {/* Pending Activity Section */}
      <PendingActivity companyId={selectedCompanyId || ''} />

      {/* Recent Activity Section */}
      <RecentActivity />
    </div>
  )
}
